import React from 'react'
import { Order } from '../../entities/order/Order'

import { styled, useTheme } from '@mui/material/styles'
import ProductItem from '../product-item/ProductItem'
import IconButton from '@mui/material/IconButton'
import ConsolidateIcon from '@mui/icons-material/MergeRounded'
import messages from './CycleOrder.messages'
import { useIntl } from 'react-intl'
import OrderConsolidateModal from '../order-consolidate-modal/OrderConsolidateModalContainer'
import ChangeOrderStatusButton from '../change-order-status-button/ChangeOrderStatusButtonContainer'
import OrderStatus from '../order-status/OrderStatus'
import { useTransition, animated, useSpring } from 'react-spring'
import { OrderProduct } from '../../entities/order-product/OrderProduct'
import CycleOrderPlaceholder from './CycleOrderPlaceholder'

export interface IOwnProps {
    order: Order
    className?: string
}

export interface IStateProps {
    productsInProcessing: OrderProduct[]
    lastAffectedOrderId: number | null
    notProcessedOrderId?: number
}

export interface IDispatchProps {
    resetLastAffectedOrder: () => void
    resetFailedOrderId: () => void
}

export type IProps = IOwnProps & IStateProps & IDispatchProps

const Title = styled('h4')(({ theme }) => ({
    display: 'flex',
    color: theme.palette.blackColor.light,
    fontSize: 25,
    margin: 0,
    marginBottom: theme.spacing(3),
    '& span': {
        fontWeight: 700,
        marginRight: theme.spacing(2),
    },
}))

const TitleWrapper = styled('div')({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
})

const ProductList = styled('ul')({
    listStyle: 'none',
    margin: 0,
    padding: 0,
})

const OrderAnimatedItem = styled(animated.article)(({ theme }) => ({
    color: theme.palette.blackColor.light,
    background: 'transparent',
}))

const ProductListItem = styled(animated.li)(({ theme }) => ({
    '& + &': {
        marginTop: theme.spacing(4),
    },
}))

const OrderIdContainer = styled(animated.span)({
    display: 'inline-block',
})

const ConsolidateButton = styled(IconButton)({
    flexShrink: 0,
    width: 26,
    height: 26,
})

const OrderToolbar = styled('div')({
    flexShrink: 0,
    marginLeft: 'auto',
    minWidth: 80,
    display: 'flex',
    justifyContent: 'center',
})

const CycleOrder: React.FC<IProps> = (props) => {
    const {
        order,
        className,
        productsInProcessing,
        lastAffectedOrderId,
        notProcessedOrderId,
        resetLastAffectedOrder,
        resetFailedOrderId,
    } = props

    const [isConsolidateModalOpen, setIsConsolidateModalOpen] =
        React.useState(false)
    const intl = useIntl()
    const products = order.products.filter(
        (product) => !productsInProcessing.includes(product)
    )
    const shouldHighlightOrderId =
        notProcessedOrderId === order.orderId && order.isIncoming()
    const shouldScrollOrderIntoView =
        lastAffectedOrderId === order.orderId ||
        notProcessedOrderId === order.orderId

    const [transition] = useTransition(
        products,
        {
            initial: {
                transform: 'translateX(0%)',
            },
            from: {
                transform: order.cycle?.isInIncoming
                    ? 'translateX(150%)'
                    : 'translateX(-150%)',
            },
            enter: {
                transform: 'translateX(0%)',
            },
            leave: {
                transform: order.cycle?.isInIncoming
                    ? 'translateX(150%)'
                    : 'translateX(-150%)',
            },
            keys: products.map(
                (product) => `${product.order?.orderId}-${product.itemId}`
            ),
            config: {
                duration: 300,
            },
        },
        [products.map((item) => item.id)]
    )

    const theme = useTheme()
    const [lastAffectedOrderStyles] = useSpring(
        {
            from: {
                backgroundColor: theme.palette.primary.dark,
            },
            to: {
                backgroundColor: 'transparent',
            },
            config: {
                duration: 700,
            },
            onResolve: () =>
                lastAffectedOrderId === order.orderId &&
                resetLastAffectedOrder(),
        },
        []
    )

    const [highlightedOrderIdStyles] = useSpring(
        {
            x: shouldHighlightOrderId ? 1 : 0,
            config: {
                duration: 400,
            },
            onResolve: () => shouldHighlightOrderId && resetFailedOrderId(),
        },
        [notProcessedOrderId]
    )

    const handleConsolidateClick = () => {
        setIsConsolidateModalOpen(true)
    }

    const handleConsolidateModalClose = () => {
        setIsConsolidateModalOpen(false)
    }

    const orderDomElementRef = React.useRef<HTMLElement>(null)

    React.useEffect(() => {
        if (shouldScrollOrderIntoView && orderDomElementRef.current) {
            orderDomElementRef.current.scrollIntoView()
        }
    }, [shouldScrollOrderIntoView, order.orderId, resetLastAffectedOrder])

    return (
        <>
            <OrderAnimatedItem
                className={className}
                ref={orderDomElementRef}
                style={
                    lastAffectedOrderId === order.orderId
                        ? lastAffectedOrderStyles
                        : {}
                }
            >
                <Title>
                    <TitleWrapper>
                        <OrderIdContainer
                            style={{
                                transform: highlightedOrderIdStyles.x
                                    .to({
                                        range: [0, 0.3, 0.65, 1],
                                        output: [0, 14, 10, 12],
                                    })
                                    .to((x) => `translateX(${x}px)`),
                                color: highlightedOrderIdStyles.x.to({
                                    range: [0, 0.5, 1],
                                    output: [
                                        theme.palette.text.secondary,
                                        theme.palette.error.main,
                                        theme.palette.error.main,
                                    ],
                                }),
                                textDecoration: order.isCanceled
                                    ? 'line-through'
                                    : '',
                            }}
                        >
                            #{order.orderId}
                        </OrderIdContainer>
                        <OrderStatus
                            status={{
                                code: order.statusCode,
                                text: order.status,
                            }}
                        />
                        <ChangeOrderStatusButton order={order} />
                    </TitleWrapper>
                    {order.canConsolidate() ? (
                        <OrderToolbar>
                            <ConsolidateButton
                                onClick={handleConsolidateClick}
                                color='primary'
                                aria-label={intl.formatMessage({
                                    ...messages.consolidate,
                                })}
                                size='small'
                            >
                                <ConsolidateIcon />
                            </ConsolidateButton>
                        </OrderToolbar>
                    ) : null}
                </Title>

                <ProductList>
                    {productsInProcessing && products.length ? (
                        transition((styles, product) => (
                            <ProductListItem
                                key={`${product.order?.orderId}-${product.itemId}`}
                                style={styles}
                            >
                                <ProductItem
                                    product={product}
                                    hasNextConsolidatedProduct={
                                        product.hasNextConsolidatedProduct
                                    }
                                />
                            </ProductListItem>
                        ))
                    ) : (
                        <CycleOrderPlaceholder />
                    )}
                </ProductList>
            </OrderAnimatedItem>
            {order.canConsolidate() && (
                <OrderConsolidateModal
                    onClose={handleConsolidateModalClose}
                    order={order}
                    open={isConsolidateModalOpen}
                />
            )}
        </>
    )
}

export default styled(CycleOrder)(({ theme }) => ({
    marginBottom: theme.spacing(5),
}))
